import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Search, Users, UserPlus, Mail } from 'lucide-react'

function initials(name: string) {
  return (name || '?').split(' ').filter(Boolean).map(s => s[0]).join('').slice(0, 2).toUpperCase()
}

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-emerald-100 text-emerald-700',
  suspended: 'bg-red-100 text-red-700',
  inactive:  'bg-gray-100 text-gray-500',
}

export function OrgUsersView() {
  const { orgId } = useOrgPermissions()
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('editor')

  const { data: orgBrand } = useQuery({
    queryKey: ['org-brand-name', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.from('organizations' as any).select('name').eq('id', orgId!).single() as any)
      return data as { name: string } | null
    },
  })

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await supabase.functions.invoke('send-team-invitation', {
        body: { email: inviteEmail.trim(), role: inviteRole, org_name: orgBrand?.name ?? '' },
      })
      if (res.error) throw res.error
    },
    onSuccess: () => {
      toast({ title: 'Invitation sent', description: `Invite sent to ${inviteEmail}` })
      setInviteEmail('')
      setInviteRole('editor')
      setInviteOpen(false)
    },
    onError: (e: any) => toast({ title: 'Failed to send invite', description: e.message, variant: 'destructive' }),
  })

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['org-users-list', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data: mList } = await (supabase
        .from('organization_members' as any)
        .select('id, user_id, status, role_id, org_unit_id, created_at, roles(name, display_name), org_units(name)')
        .eq('org_id', orgId!)
        .order('created_at', { ascending: true }) as any)

      const list = (mList ?? []) as any[]
      if (!list.length) return []

      const ids = list.map((m: any) => m.user_id).filter(Boolean)
      const { data: profs } = await supabase
        .from('profiles' as any)
        .select('user_id, first_name, last_name, full_name, avatar_url, email')
        .in('user_id', ids) as any

      const pmap: Record<string, any> = {}
      ;(profs ?? []).forEach((p: any) => {
        const composed = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
        pmap[p.user_id] = {
          name:   composed || p.full_name || p.email || p.user_id?.slice(0, 8),
          email:  p.email,
          avatar: p.avatar_url,
        }
      })

      return list.map((m: any) => ({
        ...m,
        display_name: pmap[m.user_id]?.name ?? m.user_id?.slice(0, 8),
        email:        pmap[m.user_id]?.email,
        avatar_url:   pmap[m.user_id]?.avatar,
      }))
    },
  })

  const filtered = useMemo(() => {
    if (!search.trim()) return members
    const q = search.toLowerCase()
    return (members as any[]).filter((m: any) =>
      (m.display_name ?? '').toLowerCase().includes(q) ||
      (m.email ?? '').toLowerCase().includes(q) ||
      (m.roles?.display_name ?? m.roles?.name ?? '').toLowerCase().includes(q) ||
      (m.org_units?.name ?? '').toLowerCase().includes(q)
    )
  }, [members, search])

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#0D2240] flex items-center justify-center">
            <Users className="w-4 h-4 text-[#C8A84B]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Organisation Users</h2>
            <p className="text-muted-foreground text-sm">All members of your organisation.</p>
          </div>
        </div>

        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <UserPlus className="w-4 h-4 mr-2" /> Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>Email address</Label>
                <Input
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => inviteMutation.mutate()}
                  disabled={!inviteEmail.trim() || inviteMutation.isPending}
                >
                  {inviteMutation.isPending
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Mail className="w-4 h-4 mr-2" />
                  }
                  Send Invitation
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, email or role…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading members…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {search ? 'No members match your search.' : 'No members yet.'}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(filtered as any[]).map((m: any) => (
            <Card key={m.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0D2240]/10 flex items-center justify-center shrink-0 overflow-hidden text-[11px] font-bold text-[#0D2240]">
                  {m.avatar_url
                    ? <img src={m.avatar_url} className="w-full h-full object-cover" alt="" />
                    : initials(m.display_name)
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[13px] truncate">{m.display_name}</p>
                  {m.email && (
                    <p className="text-[11px] text-muted-foreground truncate">{m.email}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {(m.roles?.display_name ?? m.roles?.name) && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {m.roles?.display_name ?? m.roles?.name}
                      </Badge>
                    )}
                    {m.org_units?.name && (
                      <span className="text-[10px] text-muted-foreground">{m.org_units.name}</span>
                    )}
                    <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[m.status] ?? STATUS_COLORS.inactive}`}>
                      {m.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
